/**
 * Shared LATAM geriatric scale scoring for QuickCGA (via InteractiveScaleCard)
 * and NewConsultation persistence. Pure JavaScript: no React, storage, or HTTP.
 *
 * Persistence labels (interpretScale) are the historical NewConsultation strings.
 * InteractiveScaleCard keeps scales-data interpret* objects for on-screen text.
 */
import {
  LAWTON_ITEMS,
  GIJON_ITEMS,
  ZARIT_ITEMS,
  CHARLSON_ITEMS,
  scoreYesavageFromAnswers,
  interpretYesavageLabel,
} from "./scales-data";

export const SCALE_MAX_SCORES = {
  barthel: 100,
  mmse: 30,
  mna_sf: 14,
  yesavage: 15,
  phq9: 27,
  gad7: 21,
  katz: 6,
  lawton: 8,
  morse: 125,
  tinetti: 28,
  frail: 5,
  sarc_f: 10,
  cfs: 9,
  pfeiffer: 10,
  norton: 20,
  gijon: 25,
  zarit: 48,
  tug: 60,
  moca: 30,
  mini_cog: 1,
  cam: 1,
  charlson: 37,
  eva_dolor: 10,
  sppb: 12,
  polifarmacia_simple: 99,
};

const PHQ9_KEYS = ["phq1", "phq2", "phq3", "phq4", "phq5", "phq6", "phq7", "phq8", "phq9"];
const GAD7_KEYS = ["gad1", "gad2", "gad3", "gad4", "gad5", "gad6", "gad7"];
const CAM_KEYS = ["inicio_agudo", "inatencion", "pensamiento_desorganizado", "alteracion_conciencia"];
const SARC_F_KEYS = ["fuerza", "caminar", "silla", "escaleras", "caidas"];

function isEmptyAnswers(answers) {
  return !answers || typeof answers !== "object" || Object.keys(answers).length === 0;
}

function countMissingKeys(answers, keys) {
  if (isEmptyAnswers(answers)) return keys.length;
  return keys.filter((key) => {
    const raw = answers[key];
    return raw === undefined || raw === null || String(raw).trim() === "";
  }).length;
}

export function interpretScale(scale, value) {
  if (value === null || value === undefined) return "";
  const fn = {
    barthel: (v) =>
      v >= 90 ? "Independiente" : v >= 61 ? "Dep. leve" : v >= 41 ? "Dep. moderada" : "Dep. severa",
    mmse: (v) =>
      v >= 24 ? "Normal" : v >= 18 ? "Deterioro leve" : v >= 10 ? "Deterioro moderado" : "Severo",
    pfeiffer: (v) =>
      v <= 2
        ? "Funcionamiento intelectual intacto"
        : v <= 4
          ? "Deterioro intelectual leve"
          : v <= 7
            ? "Deterioro intelectual moderado"
            : "Deterioro intelectual severo",
    moca: (v) =>
      v >= 26
        ? "Cognición dentro de rango esperado"
        : v >= 18
          ? "Sospecha de deterioro cognitivo leve"
          : v >= 10
            ? "Deterioro cognitivo moderado"
            : "Deterioro cognitivo severo",
    mini_cog: (v) => (v === true ? "Tamizaje positivo para deterioro cognitivo" : "Tamizaje negativo"),
    cam: (v) => (v === true ? "Compatible con delirium" : "No cumple criterios de delirium"),
    yesavage: (v) => interpretYesavageLabel(v),
    phq9: (v) =>
      v <= 4
        ? "Depresión mínima"
        : v <= 9
          ? "Depresión leve"
          : v <= 14
            ? "Depresión moderada"
            : v <= 19
              ? "Depresión moderadamente grave"
              : "Depresión grave",
    gad7: (v) =>
      v <= 4
        ? "Ansiedad mínima (tamizaje negativo habitual)"
        : v <= 9
          ? "Ansiedad leve"
          : v <= 14
            ? "Ansiedad moderada; valorar impacto funcional"
            : "Ansiedad grave; considerar evaluación",
    mna_sf: (v) => (v >= 12 ? "Normal" : v >= 8 ? "Riesgo nutricional" : "Desnutrición"),
    morse: (v) => (v <= 24 ? "Riesgo bajo" : v <= 44 ? "Riesgo medio" : "Riesgo alto"),
    frail: (v) => (v === 0 ? "Robusto" : v <= 2 ? "Pre-frágil" : "Frágil"),
    cfs: (v) => {
      const n = Number(v);
      if (n === 1) return "Muy en forma";
      if (n === 2) return "Bien";
      if (n === 3) return "Bien manejado";
      if (n === 4) return "Vulnerable";
      if (n === 5) return "Fragilidad leve";
      if (n === 6) return "Fragilidad moderada";
      if (n === 7) return "Fragilidad severa";
      if (n === 8) return "Fragilidad muy severa";
      if (n === 9) return "Enfermedad terminal";
      return "";
    },
    sarc_f: (v) =>
      v <= 3 ? "Bajo riesgo de sarcopenia (tamizaje)" : v <= 6 ? "Riesgo intermedio" : "Alto riesgo de sarcopenia",
    sppb: (v) =>
      v <= 3
        ? "Discapacidad funcional severa"
        : v <= 6
          ? "Limitación funcional moderada"
          : v <= 9
            ? "Limitación leve"
            : "Funcionalidad conservada",
    charlson: (v) => (v <= 1 ? "Comorbilidad baja" : v <= 3 ? "Comorbilidad moderada" : "Comorbilidad alta"),
    polifarmacia_simple: (v) => {
      const n = Math.floor(Number(v));
      if (Number.isNaN(n)) return "";
      if (n <= 4) return "Bajo riesgo";
      if (n <= 9) return "Polifarmacia";
      return "Polifarmacia severa";
    },
    eva_dolor: (v) =>
      v === 0 ? "Sin dolor" : v <= 3 ? "Dolor leve" : v <= 6 ? "Dolor moderado" : "Dolor intenso",
    lawton: (v) => (v === 8 ? "Independiente" : v >= 5 ? "Dep. leve" : "Dep. severa"),
    katz: (v) => (v === 6 ? "Independiente" : v >= 4 ? "Dep. leve" : "Dep. severa"),
    norton: (v) => (v >= 14 ? "Riesgo mínimo" : v >= 12 ? "Riesgo evidente" : "Riesgo alto"),
    tinetti: (v) => (v >= 24 ? "Riesgo bajo" : v >= 19 ? "Riesgo moderado" : "Riesgo alto"),
    zarit: (v) => (v <= 10 ? "Sin sobrecarga" : v <= 20 ? "Sobrecarga leve" : "Sobrecarga intensa"),
    tug: (v) => {
      const s = Number(v);
      if (Number.isNaN(s)) return "";
      if (s < 10) return "Movilidad dentro de rango esperado";
      if (s < 20) return "Funcionalidad conservada";
      if (s < 30) return "Movilidad reducida; riesgo aumentado si ≥20 s";
      return "Alteración marcada; alto riesgo funcional";
    },
  };
  return fn[scale]?.(value) || "";
}

export function calculateScoreFromAnswers(scaleId, answers) {
  if (!answers || Object.keys(answers).length === 0) return null;
  if (scaleId === "yesavage") {
    return scoreYesavageFromAnswers(answers);
  }
  if (scaleId === "phq9") {
    const complete = PHQ9_KEYS.every((k) => answers?.[k] !== undefined);
    if (!complete) return null;
    return PHQ9_KEYS.reduce((sum, key) => sum + (parseInt(answers?.[key], 10) || 0), 0);
  }
  if (scaleId === "gad7") {
    const complete = GAD7_KEYS.every((k) => answers?.[k] !== undefined);
    if (!complete) return null;
    return GAD7_KEYS.reduce((sum, key) => sum + (parseInt(answers?.[key], 10) || 0), 0);
  }
  if (scaleId === "lawton") {
    return LAWTON_ITEMS.reduce((s, item) => {
      const raw = answers[item.id];
      if (raw === undefined) return s;
      const opt = item.options.find((o) => o.optId === raw || o.optId === parseInt(raw));
      return s + (opt ? opt.value : 0);
    }, 0);
  }
  if (scaleId === "gijon") {
    return GIJON_ITEMS.reduce((s, item) => {
      const raw = answers[item.id];
      if (raw === undefined) return s;
      const opt = item.options.find((o) => o.optId === raw || o.optId === parseInt(raw));
      return s + (opt ? opt.value : 0);
    }, 0);
  }
  if (scaleId === "zarit") {
    return ZARIT_ITEMS.reduce((s, item) => {
      const raw = answers[item.id];
      if (raw === undefined) return s;
      return s + (parseInt(raw, 10) || 0);
    }, 0);
  }
  if (scaleId === "tug") {
    const raw = answers?.seconds;
    if (raw === undefined || raw === "" || String(raw).trim() === "") return null;
    const n = parseFloat(String(raw).replace(",", "."));
    return Number.isNaN(n) || n < 0 ? null : n;
  }
  if (scaleId === "moca") {
    const raw = answers?.score;
    if (raw === undefined || raw === "" || String(raw).trim() === "") return null;
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return null;
    return Math.max(0, Math.min(30, n));
  }
  if (scaleId === "polifarmacia_simple") {
    const raw = answers?.num_medicamentos;
    if (raw === undefined || raw === "" || String(raw).trim() === "") return null;
    const n = parseInt(String(raw).trim(), 10);
    if (Number.isNaN(n) || n < 0 || n > 99) return null;
    return n;
  }
  if (scaleId === "cam") {
    const complete = CAM_KEYS.every((k) => answers?.[k] === "si" || answers?.[k] === "no");
    if (!complete) return null;
    const c1 = answers.inicio_agudo === "si";
    const c2 = answers.inatencion === "si";
    const c3 = answers.pensamiento_desorganizado === "si";
    const c4 = answers.alteracion_conciencia === "si";
    return c1 && c2 && (c3 || c4);
  }
  if (scaleId === "mini_cog") {
    const recallRaw = answers?.recuerdo_3_palabras;
    const recall = parseInt(recallRaw, 10);
    const hasRecall = !Number.isNaN(recall) && recall >= 0 && recall <= 3;
    const hasClock =
      answers?.reloj_normal === true ||
      answers?.reloj_normal === false ||
      answers?.reloj_normal === "true" ||
      answers?.reloj_normal === "false";
    if (!hasRecall || !hasClock) return null;
    const clockNormal = answers.reloj_normal === true || answers.reloj_normal === "true";
    if (recall === 0) return true;
    if (recall === 3) return false;
    return !clockNormal;
  }
  if (scaleId === "charlson") {
    if (!answers || typeof answers !== "object") return null;
    const total = CHARLSON_ITEMS.reduce((sum, item) => (answers[item.id] ? sum + item.weight : sum), 0);
    const any = CHARLSON_ITEMS.some((item) => !!answers[item.id]);
    return any ? total : 0;
  }
  if (scaleId === "eva_dolor") {
    const raw = answers?.score;
    if (raw === undefined || raw === "" || String(raw).trim() === "") return null;
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return null;
    return Math.max(0, Math.min(10, n));
  }
  if (scaleId === "sarc_f") {
    return SARC_F_KEYS.reduce((sum, key) => {
      const raw = answers?.[key];
      return sum + (raw === undefined ? 0 : parseInt(raw, 10) || 0);
    }, 0);
  }
  if (scaleId === "sppb") {
    const b = Number(answers?.balance);
    const balance = Number.isNaN(b) ? null : Math.max(0, Math.min(4, b));
    const gaitUnable = !!answers?.marcha_no_puede;
    const gaitSec = parseFloat(String(answers?.marcha_segundos ?? "").replace(",", "."));
    const gait = gaitUnable ? 0 : Number.isNaN(gaitSec) || gaitSec <= 0 ? null : gaitSec <= 4.82 ? 4 : gaitSec <= 6.2 ? 3 : gaitSec <= 8.7 ? 2 : 1;
    const chairUnable = !!answers?.silla_no_puede;
    const chairSec = parseFloat(String(answers?.silla_segundos ?? "").replace(",", "."));
    const chair = chairUnable ? 0 : Number.isNaN(chairSec) || chairSec <= 0 ? null : chairSec <= 11.19 ? 4 : chairSec <= 13.69 ? 3 : chairSec <= 16.69 ? 2 : 1;
    if (balance === null || gait === null || chair === null) return null;
    return balance + gait + chair;
  }
  if (scaleId === "pfeiffer") {
    return Object.values(answers).reduce((s, val) => s + (val === 0 ? 1 : 0), 0);
  }
  return Object.values(answers).reduce((s, val) => s + (parseInt(val) || 0), 0);
}

function unansweredCountForScale(scaleId, answers) {
  if (scaleId === "phq9") return countMissingKeys(answers, PHQ9_KEYS);
  if (scaleId === "gad7") return countMissingKeys(answers, GAD7_KEYS);
  if (scaleId === "cam") {
    if (isEmptyAnswers(answers)) return CAM_KEYS.length;
    return CAM_KEYS.filter((k) => answers[k] !== "si" && answers[k] !== "no").length;
  }
  if (scaleId === "sarc_f") return countMissingKeys(answers, SARC_F_KEYS);
  if (scaleId === "mini_cog") {
    if (isEmptyAnswers(answers)) return 2;
    const hasRecall = answers.recuerdo_3_palabras !== undefined && String(answers.recuerdo_3_palabras).trim() !== "";
    const hasClock =
      answers.reloj_normal === true ||
      answers.reloj_normal === false ||
      answers.reloj_normal === "true" ||
      answers.reloj_normal === "false";
    return (hasRecall ? 0 : 1) + (hasClock ? 0 : 1);
  }
  if (scaleId === "tug") return countMissingKeys(answers, ["seconds"]);
  if (scaleId === "moca" || scaleId === "eva_dolor") return countMissingKeys(answers, ["score"]);
  if (scaleId === "polifarmacia_simple") return countMissingKeys(answers, ["num_medicamentos"]);
  if (isEmptyAnswers(answers)) return 1;
  return 0;
}

export function getScaleScoreResult(scaleId, answers) {
  const score = calculateScoreFromAnswers(scaleId, answers);
  const unansweredCount = unansweredCountForScale(scaleId, answers);
  return {
    score,
    maxScore: SCALE_MAX_SCORES[scaleId] ?? null,
    interpretation: interpretScale(scaleId, score),
    completed: score !== null && score !== undefined,
    unansweredCount,
  };
}
